import styles from "../details.module.css";

const AnalysisModal = ({ data, isOpen, onClose }) => {
  if (!isOpen) return null;

  // Function to parse JSON string and handle errors
  const parseData = (dataString) => {
    if (!dataString) return null;

    try {
      // If it's already an object, return it directly
      if (typeof dataString === "object") {
        return dataString;
      }

      // If it's a string, attempt to parse it
      if (typeof dataString === "string") {
        // First remove initial and final double quotes if they exist
        let cleanString = dataString.trim();

        // If it starts and ends with double quotes, remove them
        if (cleanString.startsWith('"') && cleanString.endsWith('"')) {
          cleanString = cleanString.slice(1, -1);
        }

        // Replace escaped quotes
        cleanString = cleanString.replace(/\\"/g, '"');

        // Parse the JSON
        return JSON.parse(cleanString);
      }

      return null;
    } catch (error) {
      console.error("Error parsing AI result data:", error);
      console.log("Raw data that failed to parse:", dataString);
      return null;
    }
  };

  const parsedData = parseData(data);

  // Function to verify if data has the expected structure
  const isValidData = (data) => {
    return data && data.summary && typeof data.summary === "object";
  };

  // Display message if no valid data is available
  if (!isValidData(parsedData)) {
    return (
      <div className={styles.modalOverlay} onClick={onClose}>
        <div
          className={styles.modalContent}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.modalHeader}>
            <h2>AI Analysis</h2>
            <button className={styles.closeButton} onClick={onClose}>
              ×
            </button>
          </div>
          <div className={styles.modalBody}>
            <div className={styles.section}>
              <p>
                No analysis data available to display or the format is
                incorrect.
              </p>
              {data && (
                <div
                  style={{
                    marginTop: "10px",
                    padding: "10px",
                    background: "#f8f9fa",
                    borderRadius: "4px",
                  }}
                >
                  <strong>Raw data for debugging:</strong>
                  <pre
                    style={{
                      fontSize: "12px",
                      overflow: "auto",
                      maxHeight: "200px",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {typeof data === "string"
                      ? data
                      : JSON.stringify(data, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Function to format numbers as percentages
  const formatPercentage = (value) => {
    if (value === undefined || value === null) return "0%";
    return `${(value * 100).toFixed(1)}%`;
  };

  // Function to format decimal numbers
  const formatDecimal = (value) => {
    if (value === undefined || value === null) return "0.00";
    return value.toFixed(2);
  };

  // Function to get the CSS class for courtesy score
  const getCourtesyScoreClass = (score) => {
    if (score === undefined || score === null) return "";
    switch (score) {
      case 1:
        return styles.score1;
      case 2:
        return styles.score2;
      case 3:
        return styles.score3;
      default:
        return "";
    }
  };

  const { summary, missing, courtesy } = parsedData;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>AI Analysis</h2>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        <div className={styles.modalBody}>
          {/* Summary Section */}
          <div className={styles.section}>
            <h3>SUMMARY</h3>
            <div className={styles.grid}>
              <div className={styles.gridItem}>
                <span>Total Reference Phrases:</span>
                <strong>{summary.total_reference_phrases || 0}</strong>
              </div>
              <div className={styles.gridItem}>
                <span>Phrases Spoken:</span>
                <strong>{summary.said_count || 0}</strong>
              </div>
              {/* <div className={styles.gridItem}>
                <span>Paraphrased:</span>
                <strong>{summary.paraphrased_count || 0}</strong>
              </div> */}
              <div className={styles.gridItem}>
                <span>Partial Phrases:</span>
                <strong>{summary.partial_count || 0}</strong>
              </div>
              <div className={styles.gridItem}>
                <span>Omitted Phrases:</span>
                <strong>{summary.missed_count || 0}</strong>
              </div>
              <div className={styles.gridItem}>
                <span>Coverage Ratio:</span>
                <strong>{formatPercentage(summary.coverage_ratio)}</strong>
              </div>
              <div className={styles.gridItem}>
                <span>Average Similarity (All):</span>
                <strong>{formatDecimal(summary.similarity_mean_all)}</strong>
              </div>
              <div className={styles.gridItem}>
                <span>Average Similarity (Matched):</span>
                <strong>
                  {formatDecimal(summary.similarity_mean_matched)}
                </strong>
              </div>
            </div>
          </div>

          {/* Missing Phrases Section */}
          {missing && missing.length > 0 && (
            <div className={styles.section}>
              <h3>PHRASES ({missing.length})</h3>
              <ul className={styles.list}>
                {missing.map((phrase, index) => (
                  <li key={index} className={styles.listItem}>
                    {phrase}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Courtesy Evaluation Section */}
          {courtesy && (
            <div className={styles.section}>
              <h3>COURTESY EVALUATION</h3>
              <div className={styles.courtesy}>
                <div>
                  <span>Score:</span>
                  <strong className={getCourtesyScoreClass(courtesy.score)}>
                    {courtesy.score || 0} - {courtesy.label || "Not evaluated"}
                  </strong>
                </div>

                {courtesy.rationale && (
                  <div>
                    <span>Rationale:</span>
                    <p>{courtesy.rationale}</p>
                  </div>
                )}

                {courtesy.positive_indicators &&
                  courtesy.positive_indicators.length > 0 && (
                    <div className={styles.indicators}>
                      <span>Positive Indicators:</span>
                      <ul>
                        {courtesy.positive_indicators.map(
                          (indicator, index) => (
                            <li key={index}>{indicator}</li>
                          )
                        )}
                      </ul>
                    </div>
                  )}

                {courtesy.concerns && courtesy.concerns.length > 0 && (
                  <div className={styles.concerns}>
                    <span>Areas for Improvement:</span>
                    <ul>
                      {courtesy.concerns.map((concern, index) => (
                        <li key={index}>{concern}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalysisModal;
